import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from 'reselect';
import { 
    Container, 
    Row, 
    Col, 
    Card, 
    CardBody, 
    CardHeader, 
    Nav, 
    NavItem, 
    NavLink, 
    TabContent, 
    TabPane,
    Label,
    Input,
    Button,
    Table,
    Badge
} from 'reactstrap';
import classnames from 'classnames';
import SimpleBar from 'simplebar-react';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { 
    getDailyReport, 
    getWeeklyReport, 
    getMonthlyReport, 
    getYearlyReport, 
    getSalesDetails 
} from '../../slices/thunks';

const FinanceReports = () => {
    document.title = "Finansijski Izvještaji | Sefer";
    
    const dispatch = useDispatch();
    const baseUrl = "https://autodijelovisefer.com/assets/";
    
    const [activeTab, setActiveTab] = useState('daily');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    
    const store = createSelector(
        (state) => state.Finance,
        (state) => ({ 
            dailyReport: state.dailyReport,
            weeklyReport: state.weeklyReport,
            monthlyReport: state.monthlyReport,
            yearlyReport: state.yearlyReport,
            salesDetails: state.salesDetails,
            loading: state.loading
        })
    );

    const { dailyReport, weeklyReport, monthlyReport, yearlyReport, salesDetails, loading } = useSelector(store);

    // Set default dates based on active tab
    useEffect(() => {
        const today = new Date();
        setEndDate(formatDate(today));
        
        switch(activeTab) {
            case 'daily':
                // Same day for daily report
                setStartDate(formatDate(today));
                break;
            case 'weekly':
                // Last 3 months for weekly
                const threeMonthsAgo = new Date(today);
                threeMonthsAgo.setMonth(today.getMonth() - 3);
                setStartDate(formatDate(threeMonthsAgo));
                break;
            case 'monthly':
                // Last year for monthly
                const oneYearAgo = new Date(today);
                oneYearAgo.setFullYear(today.getFullYear() - 1);
                setStartDate(formatDate(oneYearAgo));
                break;
            case 'yearly':
                // Last 5 years for yearly
                const fiveYearsAgo = new Date(today);
                fiveYearsAgo.setFullYear(today.getFullYear() - 5);
                setStartDate(formatDate(fiveYearsAgo));
                break;
            case 'sales':
                // Last 30 days for sales details
                const thirtyDaysAgo = new Date(today);
                thirtyDaysAgo.setDate(today.getDate() - 30);
                setStartDate(formatDate(thirtyDaysAgo));
                break;
            default:
                setStartDate(formatDate(today));
        }
    }, [activeTab]);

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDateDisplay = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('bs-BA', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    const loadData = () => {
        const params = { startDate, endDate };
        
        switch(activeTab) {
            case 'daily':
                dispatch(getDailyReport(params));
                break;
            case 'weekly':
                dispatch(getWeeklyReport(params));
                break;
            case 'monthly':
                dispatch(getMonthlyReport(params));
                break;
            case 'yearly':
                dispatch(getYearlyReport(params));
                break;
            case 'sales':
                dispatch(getSalesDetails(params));
                break;
            default:
                break;
        }
    };

    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    const handleSearch = () => {
        loadData();
    };

    const calculateTotal = (data, field) => {
        if (!data || !Array.isArray(data)) return 0;
        return data.reduce((sum, item) => sum + (item[field] || 0), 0);
    };

    const renderDailyReport = () => {
        const periods = dailyReport?.periods || [];
        if (!periods || periods.length === 0) {
            return <p className="text-center text-muted py-4">Nema podataka za prikazani period.</p>;
        }

        const summary = dailyReport?.summary || {};
        const totalRevenue = summary.totalRevenue || 0;
        const totalSold = summary.totalItemsSold || 0;

        return (
            <>
                <Row className="mb-3">
                    <Col md={6}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <h5 className="text-muted mb-1">Ukupna Zarada</h5>
                                <h3 className="mb-0">{totalRevenue.toFixed(2)} KM</h3>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <h5 className="text-muted mb-1">Ukupno Prodato</h5>
                                <h3 className="mb-0">{totalSold} artikala</h3>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <SimpleBar style={{ maxHeight: "600px" }}>
                    <Table className="table-hover">
                        <thead className="table-light">
                            <tr>
                                <th>Datum</th>
                                <th className="text-end">Prodato Artikala</th>
                                <th className="text-end">Zarada</th>
                            </tr>
                        </thead>
                        <tbody>
                            {periods.map((item, key) => (
                                <tr key={key}>
                                    <td>{formatDateDisplay(item.periodStart)}</td>
                                    <td className="text-end">{item.totalItemsSold || 0}</td>
                                    <td className="text-end fw-bold">{(item.totalRevenue || 0).toFixed(2)} KM</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </SimpleBar>
            </>
        );
    };

    const renderWeeklyReport = () => {
        const periods = weeklyReport?.periods || [];
        if (!periods || periods.length === 0) {
            return <p className="text-center text-muted py-4">Nema podataka za prikazani period.</p>;
        }

        const summary = weeklyReport?.summary || {};
        const totalRevenue = summary.totalRevenue || 0;
        const totalSold = summary.totalItemsSold || 0;

        return (
            <>
                <Row className="mb-3">
                    <Col md={6}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <h5 className="text-muted mb-1">Ukupna Zarada</h5>
                                <h3 className="mb-0">{totalRevenue.toFixed(2)} KM</h3>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <h5 className="text-muted mb-1">Ukupno Prodato</h5>
                                <h3 className="mb-0">{totalSold} artikala</h3>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <SimpleBar style={{ maxHeight: "600px" }}>
                    <Table className="table-hover">
                        <thead className="table-light">
                            <tr>
                                <th>Sedmica</th>
                                <th>Period</th>
                                <th className="text-end">Prodato Artikala</th>
                                <th className="text-end">Zarada</th>
                            </tr>
                        </thead>
                        <tbody>
                            {periods.map((item, key) => (
                                <tr key={key}>
                                    <td>Sedmica {key + 1}</td>
                                    <td>
                                        {formatDateDisplay(item.periodStart)} - {formatDateDisplay(item.periodEnd)}
                                    </td>
                                    <td className="text-end">{item.totalItemsSold || 0}</td>
                                    <td className="text-end fw-bold">{(item.totalRevenue || 0).toFixed(2)} KM</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </SimpleBar>
            </>
        );
    };

    const renderMonthlyReport = () => {
        const periods = monthlyReport?.periods || [];
        if (!periods || periods.length === 0) {
            return <p className="text-center text-muted py-4">Nema podataka za prikazani period.</p>;
        }

        const summary = monthlyReport?.summary || {};
        const totalRevenue = summary.totalRevenue || 0;
        const totalSold = summary.totalItemsSold || 0;

        return (
            <>
                <Row className="mb-3">
                    <Col md={6}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <h5 className="text-muted mb-1">Ukupna Zarada</h5>
                                <h3 className="mb-0">{totalRevenue.toFixed(2)} KM</h3>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <h5 className="text-muted mb-1">Ukupno Prodato</h5>
                                <h3 className="mb-0">{totalSold} artikala</h3>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <SimpleBar style={{ maxHeight: "600px" }}>
                    <Table className="table-hover">
                        <thead className="table-light">
                            <tr>
                                <th>Mjesec</th>
                                <th className="text-end">Prodato Artikala</th>
                                <th className="text-end">Zarada</th>
                            </tr>
                        </thead>
                        <tbody>
                            {periods.map((item, key) => (
                                <tr key={key}>
                                    <td>{item.periodLabel || formatDateDisplay(item.periodStart)}</td>
                                    <td className="text-end">{item.totalItemsSold || 0}</td>
                                    <td className="text-end fw-bold">{(item.totalRevenue || 0).toFixed(2)} KM</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </SimpleBar>
            </>
        );
    };

    const renderYearlyReport = () => {
        const periods = yearlyReport?.periods || [];
        if (!periods || periods.length === 0) {
            return <p className="text-center text-muted py-4">Nema podataka za prikazani period.</p>;
        }

        const summary = yearlyReport?.summary || {};
        const totalRevenue = summary.totalRevenue || 0;
        const totalSold = summary.totalItemsSold || 0;

        return (
            <>
                <Row className="mb-3">
                    <Col md={6}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <h5 className="text-muted mb-1">Ukupna Zarada</h5>
                                <h3 className="mb-0">{totalRevenue.toFixed(2)} KM</h3>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <h5 className="text-muted mb-1">Ukupno Prodato</h5>
                                <h3 className="mb-0">{totalSold} artikala</h3>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <SimpleBar style={{ maxHeight: "600px" }}>
                    <Table className="table-hover">
                        <thead className="table-light">
                            <tr>
                                <th>Godina</th>
                                <th className="text-end">Prodato Artikala</th>
                                <th className="text-end">Zarada</th>
                            </tr>
                        </thead>
                        <tbody>
                            {periods.map((item, key) => (
                                <tr key={key}>
                                    <td>{item.periodLabel || new Date(item.periodStart).getFullYear()}</td>
                                    <td className="text-end">{item.totalItemsSold || 0}</td>
                                    <td className="text-end fw-bold">{(item.totalRevenue || 0).toFixed(2)} KM</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </SimpleBar>
            </>
        );
    };

    const renderSalesDetails = () => {
        // Handle both flat array and nested structure
        const items = salesDetails?.soldItems || salesDetails || [];
        
        if (!Array.isArray(items) || items.length === 0) {
            return <p className="text-center text-muted py-4">Nema podataka za prikazani period.</p>;
        }

        const totalRevenue = items.reduce((sum, item) => sum + (item.sellingPrice || 0), 0);
        const summary = salesDetails?.summary;

        return (
            <>
                <Row className="mb-3">
                    <Col md={6}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <h5 className="text-muted mb-1">Ukupna Zarada</h5>
                                <h3 className="mb-0">{(summary?.totalRevenue || totalRevenue).toFixed(2)} KM</h3>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <h5 className="text-muted mb-1">Ukupno Artikala</h5>
                                <h3 className="mb-0">{summary?.totalItemsSold || items.length}</h3>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <SimpleBar style={{ maxHeight: "600px" }}>
                    <Table className="table-hover">
                        <thead className="table-light">
                            <tr>
                                <th>Artikal</th>
                                <th>Datum Prodaje</th>
                                <th className="text-end">Cijena</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, key) => (
                                <tr key={key}>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            {item.mainImage && (
                                                <img 
                                                    src={baseUrl + item.mainImage} 
                                                    alt={item.name} 
                                                    className="avatar-xs rounded me-2" 
                                                />
                                            )}
                                            <div>
                                                <div className="fw-medium">{item.publicIdentifier}</div>
                                                <div className="text-muted small">{item.name}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>{formatDateDisplay(item.soldDate || item.date)}</td>
                                    <td className="text-end fw-bold">{(item.sellingPrice || 0).toFixed(2)} KM</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </SimpleBar>
            </>
        );
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Finansijski Izvještaji" pageTitle="Finansije" />
                    
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <Row className="align-items-center">
                                        <Col md={8}>
                                            <h5 className="card-title mb-0">Izvještaji o Prodaji</h5>
                                        </Col>
                                        <Col md={4}>
                                            <div className="d-flex gap-2 justify-content-end">
                                                {(activeTab === 'daily' || activeTab === 'sales') && (
                                                    <>
                                                        <div>
                                                            <Label className="form-label mb-1 small">Od</Label>
                                                            <Input 
                                                                type="date" 
                                                                value={startDate} 
                                                                onChange={(e) => setStartDate(e.target.value)}
                                                                className="form-control-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="form-label mb-1 small">Do</Label>
                                                            <Input 
                                                                type="date" 
                                                                value={endDate} 
                                                                onChange={(e) => setEndDate(e.target.value)}
                                                                className="form-control-sm"
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                                {activeTab === 'weekly' && (
                                                    <>
                                                        <div>
                                                            <Label className="form-label mb-1 small">Od</Label>
                                                            <Input 
                                                                type="date" 
                                                                value={startDate} 
                                                                onChange={(e) => setStartDate(e.target.value)}
                                                                className="form-control-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="form-label mb-1 small">Do</Label>
                                                            <Input 
                                                                type="date" 
                                                                value={endDate} 
                                                                onChange={(e) => setEndDate(e.target.value)}
                                                                className="form-control-sm"
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                                {activeTab === 'monthly' && (
                                                    <>
                                                        <div>
                                                            <Label className="form-label mb-1 small">Od (Mjesec)</Label>
                                                            <Input 
                                                                type="month" 
                                                                value={startDate ? startDate.substring(0, 7) : ''} 
                                                                onChange={(e) => setStartDate(e.target.value + '-01')}
                                                                className="form-control-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="form-label mb-1 small">Do (Mjesec)</Label>
                                                            <Input 
                                                                type="month" 
                                                                value={endDate ? endDate.substring(0, 7) : ''} 
                                                                onChange={(e) => setEndDate(e.target.value + '-01')}
                                                                className="form-control-sm"
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                                {activeTab === 'yearly' && (
                                                    <>
                                                        <div>
                                                            <Label className="form-label mb-1 small">Od (Godina)</Label>
                                                            <Input 
                                                                type="number" 
                                                                value={startDate ? startDate.substring(0, 4) : ''} 
                                                                onChange={(e) => setStartDate(e.target.value + '-01-01')}
                                                                className="form-control-sm"
                                                                min="2000"
                                                                max="2100"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="form-label mb-1 small">Do (Godina)</Label>
                                                            <Input 
                                                                type="number" 
                                                                value={endDate ? endDate.substring(0, 4) : ''} 
                                                                onChange={(e) => setEndDate(e.target.value + '-12-31')}
                                                                className="form-control-sm"
                                                                min="2000"
                                                                max="2100"
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                                <div className="align-self-end">
                                                    <Button 
                                                        color="primary" 
                                                        size="sm" 
                                                        onClick={handleSearch}
                                                        disabled={loading}
                                                    >
                                                        {loading ? 'Učitavanje...' : 'Pretraži'}
                                                    </Button>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <Nav tabs className="nav-tabs-custom nav-success mb-3">
                                        <NavItem>
                                            <NavLink
                                                style={{ cursor: "pointer" }}
                                                className={classnames({ active: activeTab === 'daily' })}
                                                onClick={() => toggleTab('daily')}
                                            >
                                                Dnevni Izvještaj
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                style={{ cursor: "pointer" }}
                                                className={classnames({ active: activeTab === 'weekly' })}
                                                onClick={() => toggleTab('weekly')}
                                            >
                                                Sedmični Izvještaj
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                style={{ cursor: "pointer" }}
                                                className={classnames({ active: activeTab === 'monthly' })}
                                                onClick={() => toggleTab('monthly')}
                                            >
                                                Mjesečni Izvještaj
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                style={{ cursor: "pointer" }}
                                                className={classnames({ active: activeTab === 'yearly' })}
                                                onClick={() => toggleTab('yearly')}
                                            >
                                                Godišnji Izvještaj
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                style={{ cursor: "pointer" }}
                                                className={classnames({ active: activeTab === 'sales' })}
                                                onClick={() => toggleTab('sales')}
                                            >
                                                Detaljne Prodaje
                                            </NavLink>
                                        </NavItem>
                                    </Nav>

                                    <TabContent activeTab={activeTab}>
                                        <TabPane tabId="daily">
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Učitavanje...</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                renderDailyReport()
                                            )}
                                        </TabPane>
                                        <TabPane tabId="weekly">
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Učitavanje...</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                renderWeeklyReport()
                                            )}
                                        </TabPane>
                                        <TabPane tabId="monthly">
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Učitavanje...</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                renderMonthlyReport()
                                            )}
                                        </TabPane>
                                        <TabPane tabId="yearly">
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Učitavanje...</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                renderYearlyReport()
                                            )}
                                        </TabPane>
                                        <TabPane tabId="sales">
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Učitavanje...</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                renderSalesDetails()
                                            )}
                                        </TabPane>
                                    </TabContent>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default FinanceReports;
